import { HttpStatusCode } from '@angular/common/http';
import {
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { GameMode } from '../../core/models/requests/game-mode.enum';
import { apiErrorDetail, isApiErrorStatus } from '../../core/models/responses/api-error-response';
import { AnswerCode } from '../../core/models/responses/answer-code.enum';
import { InteractionResponse } from '../../core/models/responses/interaction-response';
import { InteractionType } from '../../core/models/responses/interaction-type.enum';
import { QuemSouEuSessionResponse } from '../../core/models/responses/quem-sou-eu-session-response';
import { SessionStatus } from '../../core/models/responses/session-status.enum';
import { AudioService, SoundEffect } from '../../core/services/audio';
import { GameSessionService } from '../../core/services/game-session';
import { QuemSouEuGameService } from '../../core/services/quem-sou-eu-game';
import { ScreenNavigationService } from '../../core/services/screen-navigation';
import { AlertModal } from '../../shared/components/alert-modal/alert-modal';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import {
  GameResultModal,
  GameResultVariant,
} from '../../shared/components/game-result-modal/game-result-modal';
import { Spinner } from '../../shared/components/spinner/spinner';
import { Tooltip } from '../../shared/components/tooltip/tooltip';
import { WrongGuessToast } from '../../shared/components/wrong-guess-toast/wrong-guess-toast';
import { InvalidAnswerMessages } from '../../shared/constants/invalid-answer-messages';

const WRONG_GUESS_TOAST_DURATION_MS = 5000;

const MIN_INTERACTION_LENGTH = 3;
const DEFAULT_INVALID_MESSAGE = 'Pergunta inválida, tente fazer outra.';

const SESSION_ENDED_MESSAGE = 'Esta partida não está mais disponível.';
const SESSION_RELOADED_MESSAGE = 'A partida foi atualizada por outra jogada. Confira o histórico.';
const REQUEST_ERROR_MESSAGE = 'Não foi possível enviar. Tente novamente.';

/** Aviso exibido após um erro de API; `endsSession` fecha a partida ao confirmar. */
interface ErrorAlert {
  message: string;
  endsSession: boolean;
}

type HistoryItem = Pick<
  InteractionResponse,
  'type' | 'userText' | 'answerCode' | 'idMotivoRespostaInvalida'
>;

// Campos presentes tanto em InteractionResponse quanto em QuemSouEuSessionResponse.
type Counters = Pick<
  InteractionResponse,
  'questionsCount' | 'maxQuestions' | 'remainingQuestions' | 'guessCount' | 'remainingGuesses'
>;

@Component({
  selector: 'app-game-screen',
  imports: [Spinner, Tooltip, GameResultModal, WrongGuessToast, ConfirmModal, AlertModal],
  templateUrl: './game-screen.html',
  styleUrl: './game-screen.scss',
})
export class GameScreenPage {
  private readonly screenNavigation = inject(ScreenNavigationService);
  private readonly gameSession = inject(GameSessionService);
  private readonly quemSouEuGame = inject(QuemSouEuGameService);
  private readonly audio = inject(AudioService);

  protected readonly AnswerCode = AnswerCode;
  protected readonly InteractionType = InteractionType;

  protected readonly theme = computed(() => this.screenNavigation.theme()?.name ?? '');
  // `instructions` é opcional no tema (`GET /games/{idGame}/themes`).
  protected readonly instructions = computed(
    () => this.screenNavigation.theme()?.instructions ?? [],
  );

  // Contadores vêm sempre da última resposta da API.
  protected readonly questionsCount = signal(0);
  // Limites e restantes vêm da sessão (já na criação); null = ainda desconhecido.
  protected readonly maxQuestions = signal<number | null>(null);
  protected readonly remainingQuestions = signal<number | null>(null);
  // O limite nunca bloqueia o input: o mesmo campo é usado para palpites, e é o backend
  // que decide se o texto é pergunta ou palpite.
  protected readonly isQuestionLimitReached = computed(() => this.remainingQuestions() === 0);
  protected readonly guessCount = signal(0);
  private readonly remainingGuesses = signal<number | null>(null);
  protected readonly guessLimit = computed(() => {
    const remainingGuesses = this.remainingGuesses();
    return remainingGuesses === null ? null : this.guessCount() + remainingGuesses;
  });

  protected readonly history = signal<HistoryItem[]>([]);

  // Interação em trânsito ainda não tem type/answerCode (só chegam na resposta da API),
  // por isso fica fora de `history` em vez de virar um HistoryItem com campos obrigatórios vazios.
  protected readonly pendingText = signal<string | null>(null);
  protected readonly hasPendingInteraction = computed(() => this.pendingText() !== null);
  protected readonly isGivingUp = signal(false);
  private readonly isReloading = signal(false);
  // Sessão encerrada/inexistente no backend (400/404): nada mais pode ser enviado.
  private readonly isSessionUnavailable = signal(false);
  // Bloqueia input e botão "Desistir" enquanto qualquer chamada estiver em trânsito,
  // para não haver corrida entre interação e desistência (a API responde 409 nesse caso).
  protected readonly isInputDisabled = computed(
    () =>
      this.hasPendingInteraction() ||
      this.isGivingUp() ||
      this.isReloading() ||
      this.isSessionUnavailable(),
  );
  protected readonly errorAlert = signal<ErrorAlert | null>(null);
  protected readonly showGiveUpConfirm = signal(false);

  protected readonly gameResult = signal<GameResultVariant | null>(null);
  protected readonly characterName = signal<string | null>(null);
  protected readonly showWrongGuessToast = signal(false);
  private wrongGuessToastTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly questionInput = viewChild.required<ElementRef<HTMLInputElement>>('questionInput');

  constructor() {
    // Roda após o DOM refletir o estado (atributo `disabled` já removido), então o foco
    // pega tanto no início do jogo quanto sempre que o input é liberado. Com o modal de
    // resultado aberto o foco fica com ele.
    afterRenderEffect(() => {
      if (!this.isInputDisabled() && this.gameResult() === null) {
        this.questionInput().nativeElement.focus();
      }
    });
  }

  ngOnInit(): void {
    const session = this.gameSession.snapshot();
    if (session) {
      this.restore(session);
    }
  }

  protected onSubmitInteraction(event: Event, input: HTMLInputElement): void {
    event.preventDefault();

    if (this.isInputDisabled()) {
      return;
    }

    const sanitizedText = this.sanitizeText(input.value);
    if (sanitizedText.length < MIN_INTERACTION_LENGTH) {
      return;
    }

    const idSession = this.gameSession.idSession();
    if (!idSession) {
      console.error('Não há uma sessão de jogo ativa para enviar a interação.');
      return;
    }

    this.pendingText.set(sanitizedText);
    input.value = '';

    this.quemSouEuGame.sendInteraction(idSession, sanitizedText).subscribe({
      next: (response) => {
        this.history.update((history) => [...history, this.toHistoryItem(response)]);
        this.pendingText.set(null);
        this.applyCounters(response);
        this.handleInteractionResult(response);
      },
      error: (error: unknown) => {
        console.error('Não foi possível enviar a interação.', error);
        this.pendingText.set(null);
        // Devolve o texto ao input para o jogador poder reenviar sem redigitar.
        input.value = sanitizedText;
        this.handleRequestError(error);
      },
    });
  }

  protected onGiveUpClick(): void {
    if (this.isInputDisabled() || this.gameResult() !== null) {
      return;
    }

    this.showGiveUpConfirm.set(true);
  }

  protected onCancelGiveUp(): void {
    this.showGiveUpConfirm.set(false);
  }

  protected onConfirmGiveUp(): void {
    this.showGiveUpConfirm.set(false);

    if (this.isInputDisabled() || this.gameResult() !== null) {
      return;
    }

    const idSession = this.gameSession.idSession();
    if (!idSession) {
      console.error('Não há uma sessão de jogo ativa para desistir.');
      return;
    }

    this.isGivingUp.set(true);

    // A desistência não é uma interação: não entra no histórico.
    this.quemSouEuGame.giveUp(idSession).subscribe({
      next: (response) => {
        this.isGivingUp.set(false);
        this.gameSession.registerStatus(response.status);
        this.questionsCount.set(response.questionsCount);
        this.guessCount.set(response.guessCount);
        this.characterName.set(response.characterName);
        this.audio.playEffect(SoundEffect.Loss);
        this.gameResult.set('derrota');
      },
      error: (error: unknown) => {
        console.error('Não foi possível desistir da partida.', error);
        this.isGivingUp.set(false);
        this.handleRequestError(error);
      },
    });
  }

  protected onPlayAgain(): void {
    this.gameResult.set(null);
    this.characterName.set(null);
    this.screenNavigation.goToThemeSelection(this.gameSession.mode());
  }

  protected onCloseErrorAlert(): void {
    const alert = this.errorAlert();
    this.errorAlert.set(null);
    if (alert?.endsSession) {
      this.screenNavigation.goToThemeSelection(this.gameSession.mode());
    }
  }

  protected onBackToHome(): void {
    this.gameResult.set(null);
    this.characterName.set(null);
    this.screenNavigation.goToHome();
  }

  // O `status` é checado em toda resposta: um texto que parecia pergunta pode ser um palpite
  // (detectado pela IA) que encerra a partida.
  private handleInteractionResult(response: InteractionResponse): void {
    this.gameSession.registerStatus(response.status);
    switch (response.status) {
      case SessionStatus.Vitoria:
        this.audio.playEffect(SoundEffect.Win);
        this.gameResult.set('vitoria');
        return;
      case SessionStatus.Derrota:
        this.audio.playEffect(SoundEffect.Loss);
        this.characterName.set(response.characterName);
        this.gameResult.set('derrota');
        return;
      case SessionStatus.EmAndamento:
      default:
        if (response.type === InteractionType.Guess) {
          this.audio.playEffect(SoundEffect.Error);
          this.triggerWrongGuessToast();
        } else {
          this.audio.playEffect(this.getAnswerSoundEffect(response.answerCode));
        }
        return;
    }
  }

  /**
   * Erros de interação/desistência:
   * - 400 (sessão já encerrada) / 404 (sessão inexistente ou de outro jogo): a partida acaba;
   * - 409 (sessão alterada por outra requisição): no Desafio Diário recarrega a sessão;
   *   no Modo Solo o reenvio único já foi feito pelo serviço, então só avisa;
   * - demais: avisa e mantém a partida.
   */
  private handleRequestError(error: unknown): void {
    if (
      isApiErrorStatus(error, HttpStatusCode.BadRequest) ||
      isApiErrorStatus(error, HttpStatusCode.NotFound)
    ) {
      this.isSessionUnavailable.set(true);
      this.errorAlert.set({
        message: apiErrorDetail(error) ?? SESSION_ENDED_MESSAGE,
        endsSession: true,
      });
      return;
    }

    if (
      isApiErrorStatus(error, HttpStatusCode.Conflict) &&
      this.gameSession.mode() === GameMode.DailyDash
    ) {
      this.reloadSession();
      return;
    }

    this.errorAlert.set({
      message: apiErrorDetail(error) ?? REQUEST_ERROR_MESSAGE,
      endsSession: false,
    });
  }

  private reloadSession(): void {
    this.isReloading.set(true);
    this.gameSession
      .reload()
      .then((session) => {
        this.isReloading.set(false);
        this.restore(session);
        this.errorAlert.set({ message: SESSION_RELOADED_MESSAGE, endsSession: false });
      })
      .catch((error: unknown) => {
        console.error('Não foi possível recarregar a sessão.', error);
        this.isReloading.set(false);
        this.handleRequestError(error);
      });
  }

  /**
   * Monta a partida a partir da sessão do backend (criação, retomada do Desafio Diário ou
   * recarga após 409). Contadores vêm da raiz da sessão, não da lista; nenhum som é tocado.
   */
  private restore(session: QuemSouEuSessionResponse): void {
    this.history.set(session.interactions.map((interaction) => this.toHistoryItem(interaction)));
    this.applyCounters(session);
  }

  private applyCounters(counters: Counters): void {
    this.questionsCount.set(counters.questionsCount);
    this.maxQuestions.set(counters.maxQuestions);
    this.remainingQuestions.set(counters.remainingQuestions);
    this.guessCount.set(counters.guessCount);
    this.remainingGuesses.set(counters.remainingGuesses);
  }

  private triggerWrongGuessToast(): void {
    if (this.wrongGuessToastTimeoutId !== null) {
      clearTimeout(this.wrongGuessToastTimeoutId);
    }

    this.showWrongGuessToast.set(true);
    this.wrongGuessToastTimeoutId = setTimeout(() => {
      this.showWrongGuessToast.set(false);
      this.wrongGuessToastTimeoutId = null;
    }, WRONG_GUESS_TOAST_DURATION_MS);
  }

  private getAnswerSoundEffect(answerCode: AnswerCode): SoundEffect {
    switch (answerCode) {
      case AnswerCode.Yes:
        return SoundEffect.Yes;
      case AnswerCode.No:
        return SoundEffect.No;
      case AnswerCode.Invalid:
      default:
        return SoundEffect.Invalid;
    }
  }

  private toHistoryItem(response: InteractionResponse): HistoryItem {
    return {
      type: response.type,
      userText: response.userText,
      answerCode: response.answerCode,
      idMotivoRespostaInvalida: response.idMotivoRespostaInvalida,
    };
  }

  protected getInvalidAnswerMessage(item: HistoryItem): string {
    const reason = item.idMotivoRespostaInvalida;

    if (reason !== null && reason in InvalidAnswerMessages) {
      return InvalidAnswerMessages[reason];
    }

    return DEFAULT_INVALID_MESSAGE;
  }

  private sanitizeText(rawText: string): string {
    return rawText.trim().replace(/\?/g, '').trim();
  }
}
